if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.onLayoutChange = this.onLayoutChange.bind(this);
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.querySelectorAll('[data-pk-gallery-direction]').forEach((button) => {
          button.addEventListener('click', () => {
            const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')).filter(
              (thumbnail) => thumbnail.getClientRects().length > 0
            );
            if (thumbnails.length < 2) return;
            const current = thumbnails.findIndex((thumbnail) => thumbnail.querySelector('[aria-current="true"]'));
            const next = (Math.max(current, 0) + Number(button.dataset.pkGalleryDirection) + thumbnails.length) % thumbnails.length;
            this.setActiveMedia(thumbnails[next].dataset.target, false);
          });
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        this.mql.addEventListener('change', this.onLayoutChange);
      }

      disconnectedCallback() {
        this.mql.removeEventListener('change', this.onLayoutChange);
        cancelAnimationFrame(this.layoutFrame);
      }

      onLayoutChange() {
        const selected = this.elements.thumbnails?.querySelector('[aria-current="true"]')?.closest('[data-target]');
        if (!selected) return;
        const mediaId = selected.dataset.target;
        this.aligningLayout = true;
        cancelAnimationFrame(this.layoutFrame);
        this.layoutFrame = requestAnimationFrame(() => {
          this.layoutFrame = requestAnimationFrame(() => {
            if (!this.isConnected) return;
            const active = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
            const slider = this.elements.viewer.slider;
            if (active && slider) {
              this.elements.viewer.resetPages();
              slider.scrollTo({ left: this.mql.matches ? 0 : active.offsetLeft, behavior: 'instant' });
              this.elements.viewer.querySelectorAll('[data-media-id]').forEach((item) => {
                item.classList.toggle('is-active', item === active);
              });
              this.setActiveThumbnail(selected);
            }
            this.aligningLayout = false;
          });
        });
      }

      onSlideChanged(event) {
        if (this.aligningLayout || this.mql.matches) return;
        const slider = this.elements.viewer.slider;
        if (!slider) return;
        const visible = Array.from(slider.querySelectorAll('[data-media-id]')).filter((item) => item.getClientRects().length);
        const current = visible.reduce((nearest, item) =>
          !nearest || Math.abs(item.offsetLeft - slider.scrollLeft) < Math.abs(nearest.offsetLeft - slider.scrollLeft) ? item : nearest, null);
        if (!current) return;
        visible.forEach((item) => item.classList.toggle('is-active', item === current));
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${current.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
